// REST client used by the new auth surface.  Wraps a single ``axios``
// instance with:
//   - request interceptor that injects ``Authorization: Bearer <access>``
//     for non-anonymous endpoints,
//   - response interceptor that catches 401 / ``ExpiredSignatureError`` /
//     ``JWT_INVALID_TOKEN`` and triggers a single-flight refresh + replay,
//   - failure-mode logout via the ``nuxnodeAuth:logout`` window event so
//     ``useAuthProvider`` can react without a circular import.
//
// Higher-level endpoint wrappers live in ``./authApi``; this module owns
// transport semantics only.

import { getApiUrl } from '@dashboard/config';
import axios, {
  type AxiosError,
  type AxiosInstance,
  type AxiosRequestConfig,
  type InternalAxiosRequestConfig,
} from 'axios';

import { clearTokens, getAccessToken, getRefreshToken, setTokens } from './storage';

export const NUXNODE_AUTH_LOGOUT_EVENT = 'nuxnodeAuth:logout';
export const ANONYMOUS_PATHS: readonly string[] = [
  '/api/auth/login/',
  '/api/auth/register/',
  '/api/auth/token/refresh/',
  '/api/auth/token/verify/',
  '/api/auth/password/reset/',
  '/api/auth/password/set/',
  '/api/auth/methods/',
  '/api/auth/external/url/',
  '/api/auth/external/login/',
  '/api/auth/external/refresh/',
  '/api/auth/external/verify/',
];

const REFRESH_PATH = '/api/auth/token/refresh/';
const JWT_ERROR_CODES = new Set(['ExpiredSignatureError', 'JWT_INVALID_TOKEN']);

export interface RestErrorEntry {
  field: string | null;
  code: string;
  message?: string | null;
}

export interface RestEnvelope<T> {
  data: T | null;
  errors: RestErrorEntry[];
  status: number;
}

interface RetryableConfig extends InternalAxiosRequestConfig {
  /** Set after we've already replayed a request post-refresh. */
  __nuxnodeRetried?: boolean;
  /** Anonymous endpoints skip the bearer + refresh dance entirely. */
  __nuxnodeSkipAuth?: boolean;
}

/**
 * Strip the trailing ``/graphql/`` (if any) from the configured API URL so
 * the resulting origin is suitable as ``baseURL``.  When the SaaS shell
 * deploys the dashboard same-origin with the API the value is a relative
 * path (e.g. ``/graphql/``); axios then falls back to ``window.location``.
 */
export function getApiBaseUrl(): string {
  const raw = getApiUrl();

  if (typeof raw !== 'string' || raw.length === 0) {
    return '';
  }

  const trimmed = raw.replace(/\/graphql\/?$/, '');

  if (trimmed.length === 0) {
    return '/';
  }

  return trimmed;
}

function isAnonymousPath(url: string | undefined): boolean {
  if (!url) {
    return false;
  }

  const path = url.startsWith('http') ? new URL(url).pathname : url;

  return ANONYMOUS_PATHS.some((prefix) => path.endsWith(prefix));
}

function readErrors(payload: unknown): RestErrorEntry[] {
  if (!payload || typeof payload !== 'object') {
    return [];
  }

  const errors = (payload as { errors?: unknown }).errors;

  if (!Array.isArray(errors)) {
    return [];
  }

  return errors
    .filter((entry): entry is Record<string, unknown> => !!entry && typeof entry === 'object')
    .map((entry) => ({
      field: (entry.field as string | null) ?? null,
      code: (entry.code as string) ?? '',
      message: (entry.message as string | null) ?? null,
    }));
}

function isJwtRecoverable(status: number, errors: RestErrorEntry[]): boolean {
  if (status === 401) {
    return true;
  }

  return errors.some((entry) => JWT_ERROR_CODES.has(entry.code));
}

function dispatchLogout(): void {
  if (typeof window === 'undefined') {
    return;
  }

  window.dispatchEvent(new CustomEvent(NUXNODE_AUTH_LOGOUT_EVENT));
}

let refreshInflight: Promise<string | null> | null = null;

/**
 * Exposed for the Apollo HttpLink (graphql business queries also need to
 * piggy-back on the same single-flight refresh).  Resolves with the new
 * access token or ``null`` when the refresh ultimately fails.
 */
export function refreshAccessToken(): Promise<string | null> {
  if (refreshInflight) {
    return refreshInflight;
  }

  const refreshToken = getRefreshToken();

  if (!refreshToken) {
    return Promise.resolve(null);
  }

  refreshInflight = restClient
    .post<{ token?: string | null; refreshToken?: string | null }>(REFRESH_PATH, { refreshToken }, {
      __nuxnodeSkipAuth: true,
    } as RetryableConfig)
    .then((response) => {
      const next = response.data?.token ?? null;
      const nextRefresh = response.data?.refreshToken ?? refreshToken;

      if (!next) {
        clearTokens();
        dispatchLogout();

        return null;
      }

      setTokens({ accessToken: next, refreshToken: nextRefresh });

      return next;
    })
    .catch(() => {
      clearTokens();
      dispatchLogout();

      return null;
    })
    .finally(() => {
      refreshInflight = null;
    });

  return refreshInflight;
}

function buildClient(): AxiosInstance {
  const instance = axios.create({
    baseURL: getApiBaseUrl(),
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    withCredentials: false,
    validateStatus: (status) => status >= 200 && status < 500,
  });

  instance.interceptors.request.use((config) => {
    const cfg = config as RetryableConfig;

    if (cfg.__nuxnodeSkipAuth || isAnonymousPath(cfg.url)) {
      return cfg;
    }

    const token = getAccessToken();

    if (token) {
      cfg.headers.set('Authorization', `Bearer ${token}`);
    }

    return cfg;
  });

  instance.interceptors.response.use(
    async (response) => {
      const cfg = response.config as RetryableConfig;
      const errors = readErrors(response.data);

      if (
        !cfg.__nuxnodeRetried &&
        !cfg.__nuxnodeSkipAuth &&
        cfg.url !== REFRESH_PATH &&
        isJwtRecoverable(response.status, errors)
      ) {
        const refreshed = await refreshAccessToken();

        if (refreshed) {
          cfg.__nuxnodeRetried = true;
          cfg.headers?.set?.('Authorization', `Bearer ${refreshed}`);

          return instance.request(cfg);
        }
      }

      return response;
    },
    async (error: AxiosError) => {
      const cfg = error.config as RetryableConfig | undefined;
      const status = error.response?.status ?? 0;
      const errors = readErrors(error.response?.data);

      if (
        cfg &&
        !cfg.__nuxnodeRetried &&
        !cfg.__nuxnodeSkipAuth &&
        cfg.url !== REFRESH_PATH &&
        isJwtRecoverable(status, errors)
      ) {
        const refreshed = await refreshAccessToken();

        if (refreshed) {
          cfg.__nuxnodeRetried = true;
          cfg.headers?.set?.('Authorization', `Bearer ${refreshed}`);

          return instance.request(cfg);
        }
      }

      return Promise.reject(error);
    },
  );

  return instance;
}

export const restClient: AxiosInstance = buildClient();

/**
 * Convenience wrapper that flattens ``{data, errors}`` envelopes for the
 * ``authApi`` layer.  The dashboard's UI expects all auth calls to surface
 * a uniform shape regardless of HTTP status; we adopt the GraphQL-flavoured
 * envelope so the existing ``mapServerError`` helpers keep working.
 */
export async function callRest<T>(config: AxiosRequestConfig): Promise<RestEnvelope<T>> {
  try {
    const response = await restClient.request<T>(config);
    const errors = readErrors(response.data);
    const data = errors.length > 0 ? null : (response.data as T);

    return { data, errors, status: response.status };
  } catch (caught) {
    const error = caught as AxiosError;
    const status = error.response?.status ?? 0;
    const errors = readErrors(error.response?.data);

    if (errors.length === 0 && error.message) {
      errors.push({ field: null, code: 'NETWORK_ERROR', message: error.message });
    }

    return { data: null, errors, status };
  }
}
